import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { FormField } from "@/components/registration/FormField";
import { DatePickerField } from "@/components/registration/DatePickerField";
import { User, Mail, Calendar, AlertTriangle } from "lucide-react";

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: {
    first_name: string;
    last_name: string;
    birthdate: Date | null;
  };
  userEmail: string;
  onUpdateProfile: (updates: { first_name?: string; last_name?: string; birthdate?: Date | null }) => Promise<boolean>;
  onUpdateEmail: (email: string) => Promise<boolean>;
}

export const EditProfileModal = ({
  isOpen,
  onClose,
  profile,
  userEmail,
  onUpdateProfile,
  onUpdateEmail
}: EditProfileModalProps) => {
  const [firstName, setFirstName] = useState(profile.first_name);
  const [lastName, setLastName] = useState(profile.last_name);
  const [birthdate, setBirthdate] = useState<Date | undefined>(profile.birthdate || undefined);
  const [email, setEmail] = useState(userEmail);
  const [loading, setLoading] = useState(false);

  const isBirthdateChanged = () => {
    if (!birthdate && !profile.birthdate) return false;
    if (!birthdate || !profile.birthdate) return true;
    return birthdate.getTime() !== profile.birthdate.getTime();
  };

  const handleUpdateName = async () => {
    setLoading(true);
    const success = await onUpdateProfile({
      first_name: firstName,
      last_name: lastName
    });
    if (success) {
      onClose();
    }
    setLoading(false);
  };

  const handleUpdateBirthdate = async () => {
    setLoading(true);
    const success = await onUpdateProfile({
      birthdate: birthdate || null
    });
    if (success) {
      onClose();
    }
    setLoading(false);
  };

  const handleUpdateEmail = async () => {
    setLoading(true);
    const success = await onUpdateEmail(email);
    if (success) {
      onClose();
    }
    setLoading(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Profil bearbeiten</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="name" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="name" className="text-xs">Name</TabsTrigger>
            <TabsTrigger value="email" className="text-xs">E-Mail</TabsTrigger>
            <TabsTrigger value="birthdate" className="text-xs">Geburtsdatum</TabsTrigger>
          </TabsList>

          <TabsContent value="name" className="space-y-4 mt-6">
            <div className="flex items-center gap-2 mb-4">
              <User className="h-5 w-5 text-uplivion-blue" />
              <h3 className="font-semibold">Name ändern</h3>
            </div>
            
            <FormField
              id="firstName"
              label="Vorname"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Ihr Vorname"
            />
            
            <FormField
              id="lastName"
              label="Nachname"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Ihr Nachname"
            />

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Abbrechen
              </Button>
              <Button 
                onClick={handleUpdateName} 
                disabled={loading}
                className="flex-1 bg-uplivion-blue hover:bg-blue-700"
              >
                {loading ? "Speichern..." : "Speichern"}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="email" className="space-y-4 mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Mail className="h-5 w-5 text-uplivion-blue" />
              <h3 className="font-semibold">E-Mail-Adresse ändern</h3>
            </div>
            
            <FormField
              id="email"
              label="E-Mail-Adresse"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ihre.email@beispiel.de"
            />

            <p className="text-sm text-gray-600">
              Sie erhalten eine Bestätigungs-E-Mail an die neue Adresse.
            </p>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Abbrechen
              </Button>
              <Button 
                onClick={handleUpdateEmail} 
                disabled={loading || email === userEmail}
                className="flex-1 bg-uplivion-blue hover:bg-blue-700"
              >
                {loading ? "Senden..." : "Bestätigung senden"}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="birthdate" className="space-y-4 mt-6">
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="h-5 w-5 text-uplivion-blue" />
              <h3 className="font-semibold">Geburtsdatum ändern</h3>
            </div>

            {isBirthdateChanged() && (
              <Alert className="border-orange-200 bg-orange-50">
                <AlertTriangle className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-800">
                  <strong>Wichtiger Hinweis:</strong> Wenn Sie Ihr Geburtsdatum ändern, werden alle Ihre Timeline-Einträge automatisch den neuen Lebensphasen zugeordnet. Dies kann dazu führen, dass Einträge in andere Abschnitte verschoben werden.
                </AlertDescription>
              </Alert>
            )}
            
            <DatePickerField
              id="birthdate"
              label="Geburtsdatum"
              value={birthdate}
              onChange={setBirthdate}
            />

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={onClose} className="flex-1">
                Abbrechen
              </Button>
              <Button 
                onClick={handleUpdateBirthdate} 
                disabled={loading}
                className="flex-1 bg-uplivion-blue hover:bg-blue-700"
              >
                {loading ? "Speichern..." : "Speichern"}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};